import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Spin } from 'antd';
import { ArrowDownOutlined } from '@ant-design/icons';
import { useTranslation } from '../../i18n';
import type { ChatMessage, ChatParticipant, ChatStatusLabels } from './chatTypes';
import { buildTimeline } from './chatUtils';
import { ChatBubble } from './ChatBubble';

const NEAR_BOTTOM_PX = 80;
const LOAD_EARLIER_PX = 40;

type ChatMessageListProps = {
  messages: ChatMessage[];
  self: ChatParticipant;
  peer: ChatParticipant;
  hasMore?: boolean;
  loadingEarlier?: boolean;
  onLoadEarlier?: () => void;
  onResend?: (message: ChatMessage) => void;
  emptyText?: React.ReactNode;
  statusLabels?: ChatStatusLabels;
};

type Snapshot = { firstKey?: string; lastKey?: string; scrollHeight: number };

function messageKey(message: ChatMessage | undefined) {
  return message ? message.clientId ?? message.id : undefined;
}

export function ChatMessageList({
  messages,
  self,
  peer,
  hasMore = false,
  loadingEarlier = false,
  onLoadEarlier,
  onResend,
  emptyText,
  statusLabels,
}: ChatMessageListProps) {
  const { t } = useTranslation();
  const resolvedEmpty = emptyText ?? t('chat.empty');
  const scrollRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);
  const snapshotRef = useRef<Snapshot>({ scrollHeight: 0 });
  const [unseenCount, setUnseenCount] = useState(0);

  const timeline = useMemo(() => buildTimeline(messages), [messages, t]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const prev = snapshotRef.current;
    const firstKey = messageKey(messages[0]);
    const lastKey = messageKey(messages[messages.length - 1]);
    const lastMessage = messages[messages.length - 1];

    const replacedAll = firstKey !== prev.firstKey && lastKey !== prev.lastKey;
    const prepended = !replacedAll && firstKey !== prev.firstKey && lastKey === prev.lastKey;
    const appended = !replacedAll && lastKey !== prev.lastKey;

    if (replacedAll) {
      el.scrollTop = el.scrollHeight;
      nearBottomRef.current = true;
      setUnseenCount(0);
    } else if (prepended) {
      el.scrollTop += el.scrollHeight - prev.scrollHeight;
    } else if (appended) {
      if (nearBottomRef.current || lastMessage?.direction === 'out') {
        el.scrollTop = el.scrollHeight;
        setUnseenCount(0);
      } else {
        setUnseenCount((count) => count + 1);
      }
    }

    snapshotRef.current = { firstKey, lastKey, scrollHeight: el.scrollHeight };
  }, [messages]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    nearBottomRef.current = distanceToBottom < NEAR_BOTTOM_PX;
    if (nearBottomRef.current && unseenCount > 0) {
      setUnseenCount(0);
    }
    if (el.scrollTop < LOAD_EARLIER_PX && hasMore && !loadingEarlier && onLoadEarlier) {
      snapshotRef.current.scrollHeight = el.scrollHeight;
      onLoadEarlier();
    }
  };

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
    setUnseenCount(0);
  };

  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={scrollRef}
        className="chat-scroll h-full overflow-y-auto bg-[#f7f8fb] px-5 py-4"
        onScroll={handleScroll}
      >
        {loadingEarlier ? (
          <div className="flex justify-center py-2">
            <Spin size="small" />
          </div>
        ) : null}
        {!hasMore && messages.length > 0 ? (
          <div className="py-2 text-center text-[11px] text-slate-300">{t('chat.no_earlier')}</div>
        ) : null}

        {messages.length === 0 && !loadingEarlier ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-400">
            {resolvedEmpty}
          </div>
        ) : null}

        {timeline.map((item) =>
          item.kind === 'divider' ? (
            <div key={item.key} className="my-3 flex justify-center">
              <span className="rounded-full bg-white px-3 py-0.5 text-[11px] text-slate-400 shadow-[0_0_0_1px_#eef0f4]">
                {item.label}
              </span>
            </div>
          ) : (
            <ChatBubble
              key={item.key}
              message={item.message}
              participant={item.message.direction === 'out' ? self : peer}
              isRunEnd={item.isRunEnd}
              onResend={onResend}
              statusLabels={statusLabels}
            />
          ),
        )}
      </div>

      {unseenCount > 0 ? (
        <button
          type="button"
          className="absolute bottom-3 left-1/2 inline-flex -translate-x-1/2 cursor-pointer items-center gap-1 rounded-full border-0 bg-[#596ae8] px-3 py-1 text-xs text-white shadow-[0_2px_8px_rgba(89,106,232,0.35)]"
          onClick={scrollToBottom}
        >
          <ArrowDownOutlined />
          {t('chat.new_messages', { count: unseenCount })}
        </button>
      ) : null}
    </div>
  );
}
